import { useEffect, useState, useRef } from "react";
import { X, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

const STORAGE_KEY = "af-exit-dismissed";
const DISMISS_DAYS = 14;

function shouldShow() {
  const v = localStorage.getItem(STORAGE_KEY);
  if (!v) return true;
  return Date.now() - Number(v) > DISMISS_DAYS * 86400000;
}

export default function ExitIntent() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [freq, setFreq] = useState("weekly");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    if (!shouldShow()) return;

    // Exit intent: cursor leaves viewport toward top
    const onMouseLeave = (e) => {
      if (e.clientY <= 10 && !triggered.current) {
        triggered.current = true;
        setVisible(true);
      }
    };

    // Mobile fallback: show after 45s on page
    const timer = setTimeout(() => {
      if (!triggered.current && shouldShow()) {
        triggered.current = true;
        setVisible(true);
      }
    }, 45000);

    document.addEventListener("mouseleave", onMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  };

  const subscribe = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/blog/subscribe", { email: email.trim(), frequency: freq });
      setDone(true);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={dismiss}>
      <div
        className="relative w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border-strong)] shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={dismiss} className="absolute top-3 right-3 txt-muted hover:text-white">
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="text-center py-4">
            <div className="text-2xl mb-2">✓</div>
            <div className="font-section text-white mb-1">You're in.</div>
            <div className="txt-secondary text-sm">Daily Nifty market update hits your inbox tomorrow at 8am IST.</div>
            <Button size="sm" className="mt-4 rounded-none w-full" onClick={dismiss}>Close</Button>
          </div>
        ) : (
          <>
            <div className="overline text-emerald-400 mb-1">Free Market Intelligence</div>
            <h3 className="font-section text-white text-lg mb-1">
              Get the daily Nifty update before market open
            </h3>
            <p className="txt-secondary text-sm mb-5">
              AI-curated options sentiment, PCR, IV Rank, and key levels — delivered free every morning at 8am IST.
              No spam. Unsubscribe anytime.
            </p>

            <div className="flex gap-2 mb-3">
              {["daily", "weekly"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFreq(f)}
                  className={`text-xs font-section tracking-wider px-3 py-1.5 border flex-1 transition-colors ${
                    freq === f
                      ? "border-emerald-500 text-emerald-400 bg-emerald-500/10"
                      : "border-[var(--border)] txt-muted"
                  }`}
                >
                  {f === "daily" ? "Daily (weekdays)" : "Weekly digest"}
                </button>
              ))}
            </div>

            <div className="flex gap-2 mb-3">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && subscribe()}
                placeholder="your@email.com"
                type="email"
                className="rounded-none bg-[var(--bg-surface-2)] border-[var(--border)] h-9 flex-1"
                autoFocus
              />
              <Button onClick={subscribe} disabled={submitting || !email.trim()} className="h-9 rounded-none px-4">
                <Mail className="w-4 h-4 mr-1.5" />
                {submitting ? "…" : "Subscribe"}
              </Button>
            </div>

            <div className="text-center">
              <span className="txt-muted text-xs">Or</span>{" "}
              <a
                href="https://t.me/AlgoForgeIndia"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:underline inline-flex items-center gap-1"
                onClick={dismiss}
              >
                <Send className="w-3 h-3" /> Join our Telegram channel
              </a>
            </div>

            <button onClick={dismiss} className="block w-full text-center txt-muted text-xs mt-3 hover:text-white transition-colors">
              No thanks, I'll miss out
            </button>
          </>
        )}
      </div>
    </div>
  );
}
